import { useEffect } from 'react';
import { createFileRoute, redirect, Outlet } from '@tanstack/react-router';
import { AppSidebar, TopHeader } from '@/components/navigation';
import { ImportBanner } from '@/components/campaign/ImportBanner';
import { NoAccessBanner } from '@/components/campaign';
import { useCampaignDeepLink, useMayManageCampaign } from '@/hooks';
import { useImportProgress } from '@/hooks/queries';
import { useCampaignStore } from '@/stores';
import { useImportProgressStore } from '@/stores/importProgressStore';
import { CapabilityTier } from '@signalvote/shared';

export const Route = createFileRoute('/admin')({
  beforeLoad: ({ context, location }) => {
    if (!context.auth.user) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    // Platform admins always have access
    if (context.auth.user.isPlatformAdmin) return;
    // Campaign owners and users who can reach at least one /admin child surface
    // can enter the shell. Every /admin child route and every sidebar link is
    // individually capability-gated, so admitting a Finance:View user only lets
    // them reach the finance surfaces they're already authorized for — it stops
    // the Finance nav link dead-ending for a true ViewOnly user (Finance:View,
    // no TeamManagement).
    const { hasCapability } = useCampaignStore.getState();
    if (
      hasCapability('TeamManagement', CapabilityTier.View) ||
      hasCapability('Finance', CapabilityTier.View) ||
      hasCapability('Fundraising', CapabilityTier.View)
    ) {
      return;
    }
    throw redirect({ to: '/app' });
  },
  component: AdminLayout,
});

function AdminLayout() {
  // SSE import progress → Zustand store sync
  const sseGeneration = useImportProgressStore((s) => s.sseGeneration);
  const setProgress = useImportProgressStore((s) => s.setProgress);
  const mayImport = useMayManageCampaign();
  const { progress } = useImportProgress(sseGeneration, mayImport);
  useEffect(() => { setProgress(progress); }, [progress, setProgress]);

  // Email deep-link auto-switch also runs on /admin/* so links like
  // /admin/finance?campaignId=<guid> resolve correctly.
  useCampaignDeepLink();

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Top header with logo, campaign selector, user menu */}
      <TopHeader />
      <NoAccessBanner />
      <ImportBanner />

      {/* Main content area with sidebar */}
      <div className="flex flex-1 overflow-hidden">
        <AppSidebar />
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
